import type { ReactNode } from 'react'
import { CalendarPlus, Check, ChevronDown } from 'lucide-react'
import { ButtonGlyph, buttonClass, HelpIcon } from '../../core/index.js'
import type { SchedulePeriodView } from '../types.js'
import { formatInZone, nobodyBids, preferencesWindowLine, readinessTiles, relativeDay, timelineSteps, zoneAbbreviation, type ReadinessTile, type TileTone, type TimelineStep } from './monthReadinessData.js'
import { periodStatusChip } from './scheduleRunText.js'

const TONE_DOT: Record<TileTone, string | null> = { none: null, act: 'bg-gold', problem: 'bg-coral' }
const TONE_SUB: Record<TileTone, string> = { none: 'text-ink-mid', act: 'font-bold text-ink', problem: 'font-bold text-coral' }
const TONE_LABEL: Record<TileTone, string> = { none: '', act: 'Something you can act on', problem: 'This will change the build' }

/**
 * One count. Its colour is only ever a dot and its sub-line - gold for something staff can still act on, coral for
 * something that will change the build - and the tile whose detail is open below wears a marine border and says so.
 */
function Tile({ tile, active, onOpen }: { tile: ReadinessTile; active: boolean; onOpen?: (tile: ReadinessTile) => void }) {
  const dot = TONE_DOT[tile.tone]
  const body = (
    <>
      <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
        {dot && <span className={`h-2 w-2 rounded-full ${dot}`} title={TONE_LABEL[tile.tone]} aria-label={TONE_LABEL[tile.tone]} />}
        {tile.label}
      </div>
      <div className="text-xl font-black leading-tight text-ink">{tile.value}</div>
      <div className={`text-[10px] leading-snug ${TONE_SUB[tile.tone]}`}>{tile.sub}</div>
      {active && (
        <div className="mt-1 flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-marine">
          Showing below <ChevronDown className="h-3 w-3" aria-hidden />
        </div>
      )}
    </>
  )
  const frame = `rounded border bg-white p-2.5 text-left ${active ? 'border-marine ring-1 ring-marine' : 'border-marine/15'}`
  if (!onOpen) {
    return <div className={frame}>{body}</div>
  }
  return (
    <button type="button" aria-pressed={active} onClick={() => onOpen(tile)} className={`${frame} transition-colors hover:border-marine`}>
      {body}
    </button>
  )
}

function StepDot({ state }: { state: TimelineStep['state'] }) {
  if (state === 'done') {
    return (
      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-marine text-white">
        <Check className="h-2.5 w-2.5" aria-hidden />
      </span>
    )
  }
  return <span className={`h-4 w-4 rounded-full border-2 ${state === 'next' ? 'border-coral bg-coral' : 'border-marine/30 bg-white'}`} />
}

/** The month's four dates as a stepper: done, the next one coming with how far off it is, or later, in the facility's zone. */
function TimelineStepper({ view, now }: { view: SchedulePeriodView; now: Date }) {
  const steps = timelineSteps(view, now)
  return (
    <div>
      <ol className="grid grid-cols-2 gap-y-3 sm:grid-cols-4">
        {steps.map((step, index) => (
          <li key={step.key} className="relative flex flex-col gap-1 pr-2" title={step.meaning}>
            <div className="flex items-center gap-1">
              <StepDot state={step.state} />
              {index < steps.length - 1 && <span className={`hidden h-0.5 flex-1 sm:block ${step.state === 'done' ? 'bg-marine' : 'bg-marine/15'}`} />}
            </div>
            <div className={`text-[9px] font-bold uppercase tracking-wide ${step.state === 'next' ? 'text-coral' : 'text-ink-mid'}`}>{step.label}</div>
            <div className="text-[11px] text-ink">{formatInZone(step.at, view.timezone)}</div>
            <div className="text-[10px] text-ink-mid">{step.state === 'done' ? 'Done' : relativeDay(step.at, now)}</div>
            <div className="text-[10px] leading-snug text-ink-mid/80">{step.meaning}</div>
          </li>
        ))}
      </ol>
      <p className="mt-1 text-[10px] text-ink-mid/80">
        Times in {view.timezone.replace(/_/g, ' ')} ({zoneAbbreviation(view.preferencesCloseAt, view.timezone)}), the facility&apos;s zone.
      </p>
    </div>
  )
}

/**
 * The month readiness panel (docs/plans/month-readiness-plan.md): where the month stands, its four counts as tiles that
 * open the detail behind them, and the timeline as a stepper. Without `onOpen` the tiles are figures only - the
 * partner's view, which reads the counts but not who they are.
 */
export function MonthReadinessPanel({ view, now = new Date(), activeTile = null, onOpen, canRemind = false, stepper, onPublishShifts }: { view: SchedulePeriodView; /** Opens Publish shifts, offered when the month waits on its shifts alone (nobody bids). */ onPublishShifts?: () => void; /** The month navigator, shown ahead of the status. */ stepper?: ReactNode; now?: Date; /** The tile whose detail is open below. */ activeTile?: ReadinessTile['key'] | null; onOpen?: (tile: ReadinessTile) => void; /** Staff's: the preferences tile then says Remind them and opens the To remind filter. */ canRemind?: boolean }) {
  const tiles = readinessTiles(view, canRemind)
  const chip = periodStatusChip(view.status, view.bidding, view.rosterSize)
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        {stepper}
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${chip.className}`}>{chip.label}</span>
        <span className="text-ink-mid">{preferencesWindowLine(view)}</span>
        {onPublishShifts && nobodyBids(view) && (
          // Icon only (the user's call, 22 October 2026): the line beside it says what it is for; the label is its title.
          <button
            type="button"
            className={buttonClass({ size: 'sm', variant: view.shiftsPublished > 0 ? 'secondary' : 'primary', className: '!px-1.5' })}
            onClick={onPublishShifts}
            title={view.shiftsPublished > 0 ? 'Publish more shifts' : 'Publish shifts'}
            aria-label={view.shiftsPublished > 0 ? 'Publish more shifts' : 'Publish shifts'}
          >
            <ButtonGlyph icon={CalendarPlus} size="sm" />
          </button>
        )}
        <HelpIcon topic="schedule.month-readiness" />
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {tiles.map((tile) => (
          <Tile key={tile.key} tile={tile} active={tile.key === activeTile} onOpen={onOpen} />
        ))}
      </div>
      <TimelineStepper view={view} now={now} />
    </div>
  )
}
